/**
 * The live round — card-first.
 *
 * Header, then the called ball, then the cartela filling the rest. That order
 * is the order attention moves, and the card gets the space because the card is
 * what the player is actually reading.
 *
 * Every visual state here is driven by server state. There is no "I think I
 * won" — the round becomes `finished` when the server settles it, and the
 * outcome screen reports what happened rather than what the client concluded.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, intentKey } from '../api/client.ts';
import { errorMessage } from '../api/errors.ts';
import type { CardView, RoundView } from '../api/types.ts';
import { Cartela } from '../components/Cartela.tsx';
import { BallCall } from '../components/BallCall.tsx';
import { CalledBoard } from '../components/CalledBoard.tsx';
import { CartelaPicker, MAX_CARTELA_NUMBER } from '../components/CartelaPicker.tsx';
import { Confetti } from '../components/Confetti.tsx';
import { useT } from '../i18n/index.tsx';
import {
  etb,
  etbWhole,
  roundRef,
  prizeFigure,
  daysUntil,
  weekdayIndex,
  countdown,
  countdownHMS,
} from '../format.ts';
import { useRound } from '../realtime/useRound.ts';
import { haptics } from '../telegram.ts';

/**
 * Whole seconds left until `startsAt`, ticking every 200ms — the big
 * countdown for the `'starting'` phase (§ start countdown): a brief 3-second
 * flip for Normal Bingo, a full 3-minute warning for admin-started Arif
 * Bingo. Never negative: once the countdown reaches zero the round is about
 * to flip to `'live'` server-side, and this just holds at 0 until that frame
 * arrives rather than counting into negative numbers.
 */
function useCountdownSeconds(startsAt: string | null | undefined): number | null {
  const [seconds, setSeconds] = useState<number | null>(null);

  useEffect(() => {
    if (!startsAt) {
      setSeconds(null);
      return;
    }
    const target = new Date(startsAt).getTime();
    const tick = () => setSeconds(Math.max(0, Math.ceil((target - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, [startsAt]);

  return seconds;
}

/**
 * Mirrors the server's leave window (rounds.ts, `leaveWindowDeadline`) —
 * display-only, so the leave button disappears roughly when the server would
 * start refusing. The server is what actually enforces the window; this just
 * avoids showing a button that would 409. `null` on the wire means the window
 * is open for the whole waiting phase (a Normal Bingo round with no scheduled
 * start yet), so the button stays up with a plain "leave" label instead of a
 * countdown.
 */

interface RoundScreenProps {
  roundId: string;
  myUserId: string | null;
  onBack(): void;
  onBalanceChanged(): void;
  /** Navigates straight into another round — the outcome screen's next-round bar. */
  onOpenRound(roundId: string): void;
}

export function RoundScreen({
  roundId,
  myUserId,
  onBack,
  onBalanceChanged,
  onOpenRound,
}: RoundScreenProps) {
  const t = useT();
  const state = useRound(roundId, myUserId);
  const { round, calledNumbers, calledSet, cards, takenCartelas, outcome, connection } = state;

  const [picking, setPicking] = useState(false);
  const [pendingNumber, setPendingNumber] = useState<number | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);

  /**
   * Purely cosmetic. `markMode` and `manualMarks` never feed into anything
   * that decides a round — see the note atop `Cartela`. Marks are kept per
   * card (a player may hold more than one) so switching cards does not carry
   * marks from one layout onto another.
   */
  const [markMode, setMarkMode] = useState<'auto' | 'manual'>('auto');
  const [manualMarksByCard, setManualMarksByCard] = useState<Record<string, ReadonlySet<number>>>(
    {},
  );

  const lastNumber = calledNumbers.at(-1) ?? null;
  const hasJoined = cards.length > 0;
  const maxCardsPerUser = round?.maxCardsPerUser ?? 1;
  // The picker grid's own cap, not just this player's per-user limit — a
  // round phantom-filled to every cartela slot has nothing left to sell,
  // even to a player who hasn't hit maxCardsPerUser yet.
  const isFull = (round?.cardCount ?? 0) >= (round?.cartelaPoolSize ?? MAX_CARTELA_NUMBER);
  const canJoinRoom = round?.status === 'waiting' && cards.length < maxCardsPerUser;
  const canJoin = canJoinRoom && !isFull;

  /** This player's own picker numbers in this round — shown green, never grey. */
  const myCartelaNumbers = useMemo(
    () => new Set(cards.map((c) => c.entryIndex + 1)),
    [cards],
  );

  /**
   * One key per *number attempted*, not per screen visit — a retry of the
   * same number after a timeout carries the same key and the server replays
   * its first answer instead of selling a second cartela (§7.7), but tapping
   * a different number after the first was taken is a genuinely new intent
   * and needs its own key (see the note on `api.buyCartela`).
   */
  const pickCartela = useCallback(
    async (cartelaNumber: number) => {
      if (!round) return;
      setPendingNumber(cartelaNumber);
      setPickError(null);
      const key = intentKey(`buy-${roundId}-${cartelaNumber}`);
      try {
        await api.buyCartela(roundId, key, cartelaNumber);
        haptics.tap();
        // Stay on the picker (operator's request, 2026-08-26). This used to
        // close it after every purchase, so buying three cartelas meant three
        // round-trips through "Add cartela" — the picker was built for one
        // decision when it is really a shopping trip. The just-bought number
        // turns green under the player's finger via `myCartelaNumbers`, so the
        // feedback is on this screen rather than on the one it used to jump to.
        //
        // There is no explicit "done": the back chevron leaves, and hitting
        // `maxCardsPerUser` unmounts this whole branch on its own (`canJoin`
        // goes false), which lands them in the game exactly as before.
        await state.refresh();
        onBalanceChanged();
      } catch (err) {
        setPickError(errorMessage(err, t));
      } finally {
        setPendingNumber(null);
      }
    },
    [round, roundId, state, onBalanceChanged, t],
  );

  const activeCard = cards[0] ?? null;

  const leaveCard = useCallback(
    async (cardId: string) => {
      setLeaving(true);
      setPickError(null);
      try {
        await api.leaveCartela(roundId, cardId);
        haptics.tap();
        await state.refresh();
        onBalanceChanged();
      } catch (err) {
        setPickError(errorMessage(err, t));
      } finally {
        setLeaving(false);
      }
    },
    [roundId, state, onBalanceChanged, t],
  );

  const toggleManualMark = useCallback(
    (cardId: string, value: number) => {
      setManualMarksByCard((prev) => {
        const current = prev[cardId] ?? new Set<number>();
        const next = new Set(current);
        if (next.has(value)) next.delete(value);
        else next.add(value);
        return { ...prev, [cardId]: next };
      });
    },
    [],
  );

  // Only meaningful while `status === 'starting'` — `useCountdownSeconds`
  // itself no-ops (returns null) for any other status, so this is safe to
  // call unconditionally on every render, same as any other hook.
  const countdownSeconds = useCountdownSeconds(
    round?.status === 'starting' ? round.startsAt : null,
  );
  // `m:ss`, matching the join-window countdown below. Raw seconds ("179") is
  // right for a 3-second flip but illegible for Arif's 3-minute warning.
  const startCountdownLabel =
    countdownSeconds === null
      ? ''
      : `${Math.floor(countdownSeconds / 60)}:${(countdownSeconds % 60).toString().padStart(2, '0')}`;

  // The join-window countdown, now shown inside a round a player has already
  // opened, not only on the lobby row (2026-08-16) — a player who joined and
  // stayed on this screen had no way to see how long was left. Separate hook
  // call, separate target (`joinClosesAt`, not `startsAt`): the two
  // countdowns never overlap since they belong to different statuses, but
  // keeping them as distinct calls means neither has to branch on which
  // timestamp it's ticking.
  const joinClosesInSeconds = useCountdownSeconds(
    round?.status === 'waiting' ? round.joinClosesAt : null,
  );
  const joinClosesInText =
    joinClosesInSeconds === null
      ? null
      : `${Math.floor(joinClosesInSeconds / 60)}:${(joinClosesInSeconds % 60).toString().padStart(2, '0')}`;

  // Arif Bingo counts down to the scheduled start (16:00 local), not to when
  // buying closes. Whole days past the 24h mark, live `hh:mm:ss` inside it.
  const arifTarget = round?.startsAt ?? round?.joinClosesAt ?? null;
  const arifDays = round?.gameTypeSlug === 'arif' ? daysUntil(arifTarget) : null;
  const arifDayIdx = round?.gameTypeSlug === 'arif' ? weekdayIndex(arifTarget) : null;
  const arifDay = arifDays !== null && arifDayIdx !== null ? t.weekdays[arifDayIdx] : null;
  // Bound to `startsAt` (the actual start), not `joinClosesAt` — buying can
  // close well before the round itself starts, and counting down to that
  // instead left this frozen at 0:00 for the rest of the waiting phase once
  // it elapsed. `useCountdownSeconds` here only drives the per-second
  // re-render; `countdownHMS` does the actual formatting off the live clock.
  const arifFinalDayActive =
    round?.gameTypeSlug === 'arif' && round?.status === 'waiting' && arifDays === null;
  useCountdownSeconds(arifFinalDayActive ? arifTarget : null);
  const arifFinalDayText = arifFinalDayActive ? countdownHMS(arifTarget) : null;

  // What a player must mark to win this round, shown before they join (§4
  // patterns are server-side truth; the client renders the name it was sent).
  // The round room does not show the winning pattern — players already joined
  // and it is explained on the lobby row (§ the pattern is a pre-join decision,
  // not something to be reminded of mid-round).

  const headline = useMemo(() => {
    if (!round) return null;
    // The visible start countdown (§ start countdown, Normal Bingo only)
    // takes priority over every other headline — it's a brief, active
    // moment, not a passive waiting state.
    if (round.status === 'starting') return t.roundStarting;
    if (round.status === 'waiting') return t.waitingToStart;
    // Watching without having joined: the "next ball" framing below is for a
    // player with a card in play, not a spectator, so this takes priority.
    if (!hasJoined && round.status === 'live') return t.gameInProgress;
    if (round.status === 'live' && calledNumbers.length === 0) return t.firstBallSoon;
    return null;
  }, [round, hasJoined, calledNumbers.length, t]);

  if (state.loading && !round) {
    return <div className="screen screen--centred">{t.connecting}</div>;
  }

  if (!round) {
    return (
      <div className="screen screen--centred">
        <p>{t.errorNotFound}</p>
        <button type="button" className="button" onClick={onBack}>
          {t.backToLobby}
        </button>
      </div>
    );
  }

  // Shared by both layouts below — a joined player sees it above their card,
  // a spectator above the large board.
  const ballCall =
    round.status === 'starting' ? (
      <div className="startcountdown" aria-live="polite">
        <span className="startcountdown__number">{startCountdownLabel}</span>
      </div>
    ) : (
      <BallCall calledNumbers={calledNumbers} />
    );

  if (outcome) {
    return (
      <Outcome
        outcome={outcome}
        round={round}
        calledSet={calledSet}
        onBack={onBack}
        onOpenRound={onOpenRound}
      />
    );
  }

  if (canJoin && picking) {
    return (
      <div className="screen screen--picker">
        <header className="roundhead">
          <button
            type="button"
            className="roundhead__back"
            onClick={() => setPicking(false)}
            aria-label="Back"
          >
            ‹
          </button>
          <div className="roundhead__stats">
            <div className="roundhead__stat">
              <span className="roundhead__stat-label">{t.entryFee}</span>
              <strong className="roundhead__stat-value">{etb(round.entryFee)}</strong>
            </div>
          </div>
        </header>

        {pickError && <div className="banner banner--error">{pickError}</div>}

        <p className="picker__hint">{t.pickCartela}</p>

        <div className="picker__scroll">
          <CartelaPicker
            poolSize={round.cartelaPoolSize}
            takenCartelas={takenCartelas}
            myCartelaNumbers={myCartelaNumbers}
            pendingNumber={pendingNumber}
            onPick={(n) => void pickCartela(n)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="screen screen--round">
      <header className="roundhead">
        <button type="button" className="roundhead__back" onClick={onBack} aria-label="Back">
          ‹
        </button>
        <div className="roundhead__stats">
          <div className="roundhead__stat">
            <span className="roundhead__stat-label">{t.prize}</span>
            <strong className="roundhead__stat-value roundhead__stat-value--accent">
              {etbWhole(prizeFigure(round))}
            </strong>
          </div>
          <div className="roundhead__stat">
            <span className="roundhead__stat-label">{t.entryFee}</span>
            <strong className="roundhead__stat-value">{etb(round.entryFee)}</strong>
          </div>
          <div className="roundhead__stat">
            <span className="roundhead__stat-label">{t.playersLabel}</span>
            <strong className="roundhead__stat-value">{round.playerCount ?? 0}</strong>
          </div>
        </div>
      </header>

      <p className="roundhead__ref">
        #{roundRef(round.id)}
      </p>

      {connection === 'offline' && <div className="banner banner--warn">{t.offline}</div>}

      {headline && <p className="roundhead__status">{headline}</p>}
      {round.status === 'waiting' && round.gameTypeSlug === 'arif' && (arifDays !== null || arifFinalDayText) && (
        <p className="roundhead__status roundhead__status--countdown">
          {t.startsIn(
            arifDays !== null
              ? arifDay
                ? `${t.inDays(arifDays)} · ${arifDay}`
                : t.inDays(arifDays)
              : (arifFinalDayText ?? ''),
          )}
        </p>
      )}
      {round.status === 'waiting' && round.gameTypeSlug !== 'arif' && joinClosesInText && (
        <p className="roundhead__status roundhead__status--countdown">{t.closesIn(joinClosesInText)}</p>
      )}

      <main className={activeCard ? 'roundbody' : 'roundbody roundbody--spectator'}>
        {activeCard ? (
          <>
            <div className="roundbody__board">
              <CalledBoard calledSet={calledSet} lastNumber={lastNumber} />
            </div>

            <div className="roundbody__play">
              {ballCall}

              <div className="markmode" role="group" aria-label="Marking mode">
                <button
                  type="button"
                  className={`markmode__option${markMode === 'auto' ? ' markmode__option--active' : ''}`}
                  onClick={() => setMarkMode('auto')}
                >
                  {t.autoMark}
                </button>
                <button
                  type="button"
                  className={`markmode__option${markMode === 'manual' ? ' markmode__option--active' : ''}`}
                  onClick={() => setMarkMode('manual')}
                >
                  {t.manualMark}
                </button>
              </div>

              {/* Every card on the same screen, stacked top-to-bottom — a
                  player with two cartelas sees both at once instead of
                  paging through a carousel. The leave-with-refund window is
                  a property of the round (see `round.leaveDeadline`), not of
                  each card, but each card gets its own leave button. */}
              {cards.map((card, i) => (
                <JoinedCard
                  key={card.cardId}
                  card={card}
                  index={i + 1}
                  total={cards.length}
                  roundWaiting={round.status === 'waiting'}
                  leaveDeadline={round.leaveDeadline}
                  markMode={markMode}
                  calledSet={calledSet}
                  lastNumber={lastNumber}
                  manuallyMarked={manualMarksByCard[card.cardId]}
                  onToggleMark={(value) => toggleManualMark(card.cardId, value)}
                  onLeave={() => void leaveCard(card.cardId)}
                  leaving={leaving}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="roundbody__play">
            {ballCall}
            {round.status !== 'waiting' && <p>{t.watchRound}</p>}
            <CalledBoard calledSet={calledSet} lastNumber={lastNumber} large />
          </div>
        )}
      </main>

      <footer className="roundfoot">
        <span className="roundfoot__stat">{t.called(calledNumbers.length)}</span>
      </footer>

      {canJoinRoom && (
        <div className="actionbar">
          <button
            type="button"
            className="button button--primary button--wide"
            disabled={isFull}
            onClick={() => {
              if (isFull) return;
              haptics.tap();
              setPickError(null);
              setPicking(true);
              // Wake phantom test accounts (§4) now, not after the purchase
              // completes — by the time the player has picked a number
              // they're already trickling in. Fire-and-forget: the picker
              // must never wait on this.
              void api.cartelaIntent(roundId).catch(() => {});
            }}
          >
            {isFull ? t.roundFull : `${hasJoined ? t.addCartela : t.joinRound} · ${etb(round.entryFee)}`}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * How soon the next round must be before the outcome screen offers it. An
 * hour is comfortably above any Normal Bingo turnaround and well below an
 * Arif round's scheduled gap, which is measured in days.
 */
const NEXT_ROUND_MAX_WAIT_MS = 60 * 60 * 1000;

/**
 * The next joinable round of the same game type, for the outcome screen's
 * next-round bar — the moment a player is deciding whether to stay.
 *
 * Polled rather than pushed: the round that just ended is the only one this
 * screen holds a socket for, and the next one does not exist yet when the
 * outcome first renders (the runner opens it a moment after settling). A
 * 5s poll is well inside the shortest join window and costs one small request.
 *
 * Filtered to the same `gameTypeId` — a player who just finished Normal Bingo
 * is not offered Arif.
 * A failed request leaves the bar hidden rather than surfacing an error: this
 * is a convenience on top of a screen that has already said what happened.
 */
function useNextRound(finished: RoundView): RoundView | null {
  const [next, setNext] = useState<RoundView | null>(null);
  const { id, gameTypeId } = finished;

  useEffect(() => {
    // An ad-hoc admin round has no game type, so there is no "next one" to
    // point at — nothing recurring produced it.
    if (!gameTypeId) return;

    let cancelled = false;
    const load = async () => {
      try {
        const { rounds } = await api.rounds();
        const candidate =
          rounds.find(
            (r) =>
              r.id !== id &&
              r.gameTypeId === gameTypeId &&
              (r.status === 'waiting' || r.status === 'starting'),
          ) ?? null;
        if (!cancelled) setNext(candidate);
      } catch {
        // Keep whatever we last had; the bar is not worth an error state.
      }
    };
    void load();
    const poll = window.setInterval(() => void load(), 5_000);
    return () => {
      cancelled = true;
      window.clearInterval(poll);
    };
  }, [id, gameTypeId]);

  return next;
}

/**
 * The end state.
 *
 * The revealed seed is shown here and nowhere else, because here is the only
 * place it exists: until the round ends the server will not emit it, and
 * publishing it earlier would make the commitment worthless (§7.5). Surfacing
 * it is the point of commit-reveal — a player who wants to check the draws were
 * not chosen after the fact needs both halves.
 */
function Outcome({
  outcome,
  round,
  calledSet,
  onBack,
  onOpenRound,
}: {
  outcome: NonNullable<ReturnType<typeof useRound>['outcome']>;
  round: RoundView;
  calledSet: ReadonlySet<number>;
  onBack(): void;
  onOpenRound(roundId: string): void;
}) {
  const t = useT();
  const nextRound = useNextRound(round);
  // Re-render once a second so the bar's countdown ticks between the 5s polls.
  const [, setTick] = useState(0);
  useEffect(() => {
    const clock = window.setInterval(() => setTick((n) => n + 1), 1_000);
    return () => window.clearInterval(clock);
  }, []);
  const nextCloses = nextRound ? countdown(nextRound.joinClosesAt) : null;
  // Only when the next round is actually imminent. `countdown()` renders plain
  // minutes, so an Arif round scheduled two days out would read "closes in
  // 2880:00" — and a player who just finished a round is not waiting an hour
  // for the next one anyway. Past the threshold the lobby, which formats long
  // waits as days and weekdays, is the right place for it.
  const nextIsImminent =
    nextRound?.joinClosesAt != null &&
    new Date(nextRound.joinClosesAt).getTime() - Date.now() <= NEXT_ROUND_MAX_WAIT_MS;
  // Branding is specific to the two named game types — an ad-hoc admin round
  // (no game type) keeps the plain star/dot treatment it always had.
  const isArif = round.gameTypeSlug === 'arif';
  const isNormal = round.gameTypeSlug === 'normal';
  const celebrating = outcome.kind === 'won' || outcome.kind === 'lost';

  return (
    <div className="screen screen--centred outcome">
      {isNormal && celebrating && <Confetti />}

      {outcome.kind === 'won' && (
        <>
          <div className={`outcome__mark outcome__mark--win${isArif ? ' outcome__mark--trophy' : ''}`}>
            {isArif ? '🏆' : '★'}
          </div>
          <h1 className="outcome__title">{t.youWon}</h1>
          <p className="outcome__body">{t.youWonAmount(etbWhole(outcome.amount ?? null))}</p>
          {/* §4 dual winner: name whichever winner isn't the viewer. */}
          {outcome.secondWinner &&
            (outcome.youAreSecondWinner ? (
              <p className="outcome__body">
                {t.splitPotWith(
                  outcome.winnerFirstName ?? '',
                  outcome.winnerCartelaNumber ?? 0,
                  etbWhole(outcome.winnerAmount ?? null),
                )}
              </p>
            ) : (
              <p className="outcome__body">
                {t.splitPotWith(
                  outcome.secondWinner.firstName,
                  outcome.secondWinner.cartelaNumber,
                  etbWhole(outcome.secondWinner.amount),
                )}
              </p>
            ))}
        </>
      )}

      {outcome.kind === 'lost' && (
        <>
          <div className={`outcome__mark${isArif ? ' outcome__mark--trophy' : ''}`}>
            {isArif ? '🏆' : '·'}
          </div>
          {outcome.winnerFirstName ? (
            <>
              {/* Plain text, deliberately never a link — it names someone, it
                  doesn't navigate to them. */}
              <h1 className="outcome__title">
                {t.winnerAnnouncement(
                  outcome.winnerFirstName,
                  outcome.winnerCartelaNumber ?? 0,
                  etbWhole(outcome.winnerAmount ?? null),
                )}
              </h1>
              {/* §4 dual winner: a spectator or non-winner sees both names. */}
              {outcome.secondWinner && (
                <p className="outcome__body">
                  {t.splitPotWith(
                    outcome.secondWinner.firstName,
                    outcome.secondWinner.cartelaNumber,
                    etbWhole(outcome.secondWinner.amount),
                  )}
                </p>
              )}
            </>
          ) : (
            <>
              <h1 className="outcome__title">{t.roundOver}</h1>
              <p className="outcome__body">{t.someoneElseWon}</p>
            </>
          )}
        </>
      )}

      {outcome.kind === 'cancelled' && (
        <>
          <div className="outcome__mark">↺</div>
          <h1 className="outcome__title">{t.roundCancelled}</h1>
          <p className="outcome__body">{t.roundCancelledRefunded}</p>
        </>
      )}

      {/* The actual winning card(s), with the specific combo that completed
          the pattern highlighted — shown to every viewer, winner or not, not
          just "your card". A split-pot round (§4 dual winner) shows both. */}
      {celebrating && outcome.winnerLayout && (
        <div className="outcomecards">
          <div className="outcomecard">
            <span className="outcomecard__label">
              {t.yourCartelaNumber(outcome.winnerCartelaNumber ?? 0)}
            </span>
            <Cartela
              layout={outcome.winnerLayout}
              calledNumbers={calledSet}
              lastNumber={null}
              winningCells={new Set(outcome.winnerCombo ?? [])}
              dimmed
            />
          </div>
          {outcome.secondWinner && (
            <div className="outcomecard">
              <span className="outcomecard__label">
                {t.yourCartelaNumber(outcome.secondWinner.cartelaNumber)}
              </span>
              <Cartela
                layout={outcome.secondWinner.layout}
                calledNumbers={calledSet}
                lastNumber={null}
                winningCells={new Set(outcome.secondWinner.combo)}
                dimmed
              />
            </div>
          )}
        </div>
      )}

      {/* Only while there is something to join: once the countdown runs out the
          server stops accepting entries, and a bar offering a round that will
          refuse the player is worse than no bar. */}
      {nextRound && nextCloses && nextIsImminent && (
        <button
          type="button"
          className="nextround"
          onClick={() => onOpenRound(nextRound.id)}
        >
          <span className="nextround__label">{t.nextRoundJoinIn(nextCloses)}</span>
          <span className="nextround__cta">{t.joinNextRound}</span>
        </button>
      )}

      <button type="button" className="button button--wide" onClick={onBack}>
        {t.backToLobby}
      </button>
    </div>
  );
}

interface JoinedCardProps {
  card: CardView;
  index: number;
  total: number;
  roundWaiting: boolean;
  /** When the leave-with-refund window closes, or null while it is open for the whole waiting phase. */
  leaveDeadline: string | null;
  markMode: 'auto' | 'manual';
  calledSet: ReadonlySet<number>;
  lastNumber: number | null;
  manuallyMarked?: ReadonlySet<number> | undefined;
  onToggleMark(value: number): void;
  onLeave(): void;
  leaving: boolean;
}

/**
 * One of a player's cartelas in the stacked view. Each card is independent:
 * its own label, its own manual marks, and — while the round is still
 * waiting — its own leave-with-refund button. The window itself is the
 * round's (`leaveWindowDeadline` server-side): a countdown when the server
 * sent a deadline, or a plain leave label when the round has no scheduled
 * start yet and the window is open.
 */
function JoinedCard({
  card,
  index,
  total,
  roundWaiting,
  leaveDeadline,
  markMode,
  calledSet,
  lastNumber,
  manuallyMarked,
  onToggleMark,
  onLeave,
  leaving,
}: JoinedCardProps) {
  const t = useT();
  const leaveSecondsLeft = useCountdownSeconds(leaveDeadline);
  const leaveOpen =
    roundWaiting && (leaveDeadline === null || (leaveSecondsLeft ?? 0) > 0);

  return (
    <div className="joinedcard">
      {total > 1 && <p className="joinedcard__label">{t.cardOf(index, total)}</p>}
      <Cartela
        layout={card.layout}
        calledNumbers={calledSet}
        lastNumber={lastNumber}
        mode={markMode}
        manuallyMarked={manuallyMarked}
        onToggleMark={onToggleMark}
      />
      {leaveOpen && (
        <button
          type="button"
          className="button button--ghost leavecard"
          disabled={leaving}
          onClick={onLeave}
        >
          {leaveDeadline === null ? t.leaveCartelaOpen : t.leaveCartela(leaveSecondsLeft ?? 0)}
        </button>
      )}
    </div>
  );
}
